import { Box, Text, Paragraph } from '@zillow/constellation';
import React from 'react';

import AuthorAvatar from './AuthorAvatar';
import AuthorCard from './AuthorCard';
import { AUTHOR_PAGE_CONTENT_MAX_WIDTH } from './AuthorPage';
import type { AuthorHeaderProps } from './AuthorPage.types';
import ExpertiseCard from './ExpertiseCard';
import SocialLinks from './SocialLinks';
// Import the shared max width constant

/**
 * Header section for the author page, including avatar, name, job title, description, and social links.
 */
const AuthorHeader: React.FC<AuthorHeaderProps> = ({ author, socialLinks }) => (
    <Box
        sx={{
            width: '100%',
            backgroundColor: '$colors.blue600',
            paddingTop: '$spacing.xl',
            paddingBottom: '$spacing.xl',
            paddingLeft: '$spacing.md',
            paddingRight: '$spacing.md',
            position: 'relative',
            zIndex: 0,
            '@media $lg': {
                paddingLeft: '$spacing.xl',
                paddingRight: '$spacing.xl',
                minHeight: '421px',
            },
            '@media $xl': {
                paddingLeft: '$spacing.xxl',
                paddingRight: '$spacing.xxl',
            },
        }}
    >
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '$spacing.xxl',
                width: '100%',
                maxWidth: AUTHOR_PAGE_CONTENT_MAX_WIDTH, // Shared page content width
                justifyContent: 'center',
                margin: '0 auto',
            }}
        >
            <Box
                sx={{
                    display: 'none',
                    '@media $md': {
                        display: 'block',
                    },
                    '@media $lg': {
                        width: '210px',
                    },
                    '@media $xl': {
                        width: '300px',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'block',
                        '@media $lg': {
                            display: 'none',
                        },
                    }}
                >
                    <AuthorCard author={author} />
                </Box>
            </Box>
            <Box
                sx={{
                    flex: 1,
                    maxWidth: '$breakpoints.lg',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '$spacing.md',
                        marginBottom: '$spacing.lg',

                        '@media $md': {
                            marginBottom: '0',
                        },
                    }}
                >
                    <Box sx={{ '@media $md': { display: 'none' } }}>
                        <AuthorAvatar
                            avatar={author.avatar}
                            name={author.name}
                            size="xl"
                        />
                    </Box>
                    <Box>
                        <Text
                            as="h1"
                            fontType={{ default: 'h4', md: 'h3', lg: 'h2' }}
                            fontColor="textWhite"
                            marginBottom={{ default: 'xs', lg: 'md' }}
                            data-epi-type="title"
                        >
                            {author.name}
                        </Text>
                        <Box
                            as={Paragraph}
                            fontType={{ default: 'finePrint', md: 'body' }}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '$spacing.xs',
                                '@media $md': {
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: '$spacing.md',
                                },
                            }}
                            fontColor="textWhite"
                            marginBottom="md"
                        >
                            {author?.jobTitle?.toUpperCase()}
                            <SocialLinks socialLinks={socialLinks} />
                        </Box>
                    </Box>
                </Box>
                {/* @ts-expect-error - Paragraph requires children but we're using dangerouslySetInnerHTML */}
                <Paragraph
                    fontColor="textWhite"
                    marginBottom="md"
                    fontType={{ default: 'finePrint', md: 'body' }}
                    dangerouslySetInnerHTML={{ __html: author.description }}
                />
                {/* Show ExpertiseCard below md breakpoint if expertise exists */}
                <Box sx={{ '@media $md': { display: 'none' } }}>
                    {author.expertise && (
                        <ExpertiseCard expertise={author.expertise} />
                    )}
                </Box>
            </Box>
        </Box>
    </Box>
);

export default AuthorHeader;
